import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  Bell,
  Clock,
  Inbox,
  Mail,
  Newspaper,
  Receipt,
  Send,
  Settings,
  SlidersHorizontal,
  Star,
  Trash2,
  Users,
  WifiOff,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import {
  accountHue,
  api,
  type PublicAccount,
  type ThreadCategory,
  type ThreadViewName,
} from '../lib/api';
import { useUiStore } from '../lib/store';
import { useConnectionState, useSyncProgress } from '../lib/ws';
import styles from './Sidebar.module.css';

/**
 * Two surfaces, one file, because they answer the same question with different
 * budgets.
 *
 * `AppRail` is the ~40px column that is ALWAYS visible, in every state, at every
 * width. It is an app-level switcher, not folder navigation: the mark, the eight
 * mailbox dots, compose, and the two settings doors. No text labels anywhere.
 *
 * `NavDrawer` is the transient overlay that slides over the main column and
 * holds everything that used to justify a 260px permanent column — the account
 * switcher with counts, the mailboxes, the category splits. It is never a
 * column; it is opened, used and dismissed.
 */

/** Beyond this the counts show a lower bound, so keep it above anything we render. */
const UNREAD_SCAN_LIMIT = 200;
const STALE_AFTER_SEC = 10 * 60;

const VIEWS: { view: ThreadViewName; label: string; icon: LucideIcon }[] = [
  { view: 'inbox', label: 'Inbox', icon: Inbox },
  { view: 'unread', label: 'Unread', icon: Mail },
  { view: 'starred', label: 'Starred', icon: Star },
  { view: 'snoozed', label: 'Snoozed', icon: Clock },
  { view: 'sent', label: 'Sent', icon: Send },
  { view: 'archive', label: 'Archive', icon: Archive },
  { view: 'trash', label: 'Trash', icon: Trash2 },
];

const CATEGORIES: { category: ThreadCategory; label: string; icon: LucideIcon }[] = [
  { category: 'priority', label: 'Important', icon: Zap },
  { category: 'people', label: 'People', icon: Users },
  { category: 'newsletters', label: 'Newsletters', icon: Newspaper },
  { category: 'notifications', label: 'Notifications', icon: Bell },
  { category: 'receipts', label: 'Receipts', icon: Receipt },
];

type Freshness = 'ok' | 'stale' | 'error' | 'syncing' | 'off' | 'auth';

function freshnessOf(account: PublicAccount, syncing: boolean, nowSec: number): Freshness {
  if (!account.enabled || account.status === 'disabled') return 'off';
  if (account.status === 'auth_error') return 'auth';
  if (account.status === 'error') return 'error';
  if (syncing || account.status === 'connecting' || account.status === 'pending') {
    return 'syncing';
  }
  if (account.lastSyncAt === null) return 'stale';
  return nowSec - account.lastSyncAt > STALE_AFTER_SEC ? 'stale' : 'ok';
}

function freshnessTitle(state: Freshness, account: PublicAccount, nowSec: number): string {
  switch (state) {
    case 'auth':
      return account.statusDetail ?? 'Sign-in failed — reconnect this account';
    case 'error':
      return account.statusDetail ?? 'Sync error';
    case 'off':
      return 'Syncing paused';
    case 'syncing':
      return 'Syncing now';
    case 'stale':
      return account.lastSyncAt === null
        ? 'Never synced'
        : `Last synced ${Math.round((nowSec - account.lastSyncAt) / 60)} min ago`;
    case 'ok':
      return 'Synced';
  }
}

/** Ticks so "stale after 10 minutes" becomes true without a user action. */
function useNowSec(intervalMs: number): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** One scan of unread threads gives every per-account count at once. */
function useUnreadCounts() {
  const unreadQuery = useQuery({
    queryKey: ['threads', { view: 'unread', limit: UNREAD_SCAN_LIMIT }],
    queryFn: () => api.threads({ view: 'unread', limit: UNREAD_SCAN_LIMIT }),
  });

  return useMemo(() => {
    const perAccount = new Map<number, number>();
    let total = 0;
    for (const thread of unreadQuery.data?.threads ?? []) {
      total += 1;
      for (const id of thread.accountIds) {
        perAccount.set(id, (perAccount.get(id) ?? 0) + 1);
      }
    }
    return { perAccount, total };
  }, [unreadQuery.data]);
}

function useAccounts(): PublicAccount[] {
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  return useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);
}

function countLabel(n: number): string {
  return n > 99 ? '99+' : n > 0 ? String(n) : '';
}

/* ------------------------------- the rail -------------------------------- */

export function AppRail() {
  const nowSec = useNowSec(30_000);
  const connection = useConnectionState();
  const syncing = useSyncProgress();

  const accountFilter = useUiStore((s) => s.accountFilter);
  const setAccountFilter = useUiStore((s) => s.setAccountFilter);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);
  const setAppearanceOpen = useUiStore((s) => s.setAppearanceOpen);
  const openComposer = useUiStore((s) => s.openComposer);

  const accounts = useAccounts();
  const unread = useUnreadCounts();

  // Alt+1..8 jumps to an account, Alt+0 goes back to All Inboxes. The digits
  // index *enabled* accounts, matching the same shortcut in lib/keymap — two
  // handlers landing on different mailboxes would be worse than either alone.
  // This lives on the rail because the rail is the one surface that is mounted
  // in every state, which is what makes the chord global.
  const shortcutAccounts = useMemo(() => accounts.filter((a) => a.enabled), [accounts]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (!/^[0-9]$/.test(e.key)) return;
      const n = Number(e.key);
      if (n === 0) {
        e.preventDefault();
        setAccountFilter(null);
        return;
      }
      const account = shortcutAccounts[n - 1];
      if (!account) return;
      e.preventDefault();
      setAccountFilter(account.id);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcutAccounts, setAccountFilter]);

  return (
    <nav className={styles.rail} aria-label="Mailboxes">
      <img className={styles.mark} src="/icons/icon-192.png" alt="Kiln" width={20} height={20} />

      <div className={styles.railAccounts}>
        <button
          type="button"
          className={styles.railDot}
          data-active={accountFilter === null}
          data-unread={unread.total > 0}
          onClick={() => setAccountFilter(null)}
          title={`All Inboxes · Alt+0${unread.total > 0 ? ` · ${unread.total} unread` : ''}`}
          aria-label="All inboxes"
          aria-pressed={accountFilter === null}
        >
          {/* Sliced into the first few account hues, so "all" reads as a blend
              of the mailboxes rather than as a ninth colour. */}
          <span className={styles.allDot} aria-hidden="true">
            {accounts.length === 0 ? (
              <span
                className={styles.allDotSlice}
                style={{ ['--hue' as string]: 'var(--text-quaternary)' }}
              />
            ) : (
              accounts.slice(0, 4).map((a) => (
                <span
                  key={a.id}
                  className={styles.allDotSlice}
                  style={{ ['--hue' as string]: accountHue(a.id, accounts) }}
                />
              ))
            )}
          </span>
        </button>

        {accounts.map((account) => {
          const state = freshnessOf(account, syncing.has(account.id), nowSec);
          const n = unread.perAccount.get(account.id) ?? 0;
          const slot = shortcutAccounts.indexOf(account) + 1;
          const chord = slot >= 1 && slot <= 8 ? ` · Alt+${slot}` : '';
          return (
            <button
              key={account.id}
              type="button"
              className={styles.railDot}
              data-active={accountFilter === account.id}
              data-unread={n > 0}
              data-attention={state === 'auth' || state === 'error'}
              onClick={() => setAccountFilter(account.id)}
              title={`${account.email}${chord}${n > 0 ? ` · ${n} unread` : ''} · ${freshnessTitle(
                state,
                account,
                nowSec,
              )}`}
              aria-label={account.email}
              aria-pressed={accountFilter === account.id}
            >
              <span
                className={styles.hueDot}
                style={{ ['--hue' as string]: accountHue(account.id, accounts) }}
                aria-hidden="true"
              />
              {n > 0 && <span className={styles.pip} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={styles.railButton}
        data-tone="accent"
        onClick={() => openComposer()}
        title="Compose · c"
        aria-label="Compose"
      >
        <Send size={16} className="lucide" aria-hidden="true" />
      </button>

      <div className={styles.railSpacer} />

      {connection === 'offline' && (
        <span className={styles.offline} role="status" title="Reconnecting to the Kiln server">
          <WifiOff size={14} className="lucide" aria-hidden="true" />
        </span>
      )}

      <button
        type="button"
        className={styles.railButton}
        onClick={() => setAppearanceOpen(true)}
        title="Appearance — theme, density, accent, mailbox colours"
        aria-label="Appearance settings"
      >
        <SlidersHorizontal size={16} className="lucide" aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.railButton}
        onClick={() => setSettingsOpen(true)}
        title="Accounts & settings"
        aria-label="Accounts and settings"
      >
        <Settings size={16} className="lucide" aria-hidden="true" />
      </button>
    </nav>
  );
}

/* ------------------------------ the drawer ------------------------------- */

/**
 * Pin glyph. Filled when pinned so the state reads at a glance without relying
 * on the surrounding layout having already changed.
 */
function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6.2 1.8h3.6l-.5 4 2.3 2.1v1.2H4.4V7.9l2.3-2.1z"
        fill={pinned ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M8 9.1v5.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function NavDrawer({ onNavigate }: { onNavigate?: () => void }) {
  const nowSec = useNowSec(30_000);
  const syncing = useSyncProgress();

  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const category = useUiStore((s) => s.category);
  const setCategory = useUiStore((s) => s.setCategory);
  const accountFilter = useUiStore((s) => s.accountFilter);
  const setAccountFilter = useUiStore((s) => s.setAccountFilter);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);

  const accounts = useAccounts();
  const unread = useUnreadCounts();

  const drawerPinned = useUiStore((s) => s.drawerPinned);
  const setDrawerPinned = useUiStore((s) => s.setDrawerPinned);

  const go = (fn: () => void) => () => {
    fn();
    // Pinned, the drawer is a column the user chose to keep — dismissing it on
    // every navigation would defeat the entire point of pinning it.
    if (!drawerPinned) onNavigate?.();
  };

  return (
    <nav className={styles.drawer} aria-label="Mailboxes and views">
      <div className={styles.drawerHead}>
        <button
          type="button"
          className={styles.pin}
          data-on={drawerPinned}
          aria-pressed={drawerPinned}
          title={drawerPinned ? 'Unpin (⌘\\)' : 'Keep open (⌘\\)'}
          onClick={() => setDrawerPinned(!drawerPinned)}
        >
          <PinIcon pinned={drawerPinned} />
          <span className={styles.pinLabel}>{drawerPinned ? 'Pinned' : 'Keep open'}</span>
        </button>
      </div>
      <div className={styles.scroll}>
        <div className={styles.group}>
          <div className={styles.groupLabel}>Accounts</div>
          <button
            type="button"
            className={styles.row}
            data-primary="true"
            data-active={accountFilter === null}
            data-unread={unread.total > 0}
            onClick={go(() => setAccountFilter(null))}
          >
            <span className={styles.allDot} aria-hidden="true">
              {accounts.length === 0 ? (
                <span
                  className={styles.allDotSlice}
                  style={{ ['--hue' as string]: 'var(--text-quaternary)' }}
                />
              ) : (
                accounts.slice(0, 4).map((a) => (
                  <span
                    key={a.id}
                    className={styles.allDotSlice}
                    style={{ ['--hue' as string]: accountHue(a.id, accounts) }}
                  />
                ))
              )}
            </span>
            <span className={styles.label}>All Inboxes</span>
            <span className={styles.count} data-numeric>
              {countLabel(unread.total)}
            </span>
          </button>

          {accounts.map((account) => {
            const state = freshnessOf(account, syncing.has(account.id), nowSec);
            const n = unread.perAccount.get(account.id) ?? 0;
            return (
              <div key={account.id}>
                <button
                  type="button"
                  className={styles.row}
                  data-active={accountFilter === account.id}
                  data-unread={n > 0}
                  onClick={go(() => setAccountFilter(account.id))}
                  title={account.email}
                >
                  <span
                    className={styles.hueDot}
                    style={{ ['--hue' as string]: accountHue(account.id, accounts) }}
                    aria-hidden="true"
                  />
                  <span className={styles.label}>{account.displayName}</span>
                  {state === 'auth' ? (
                    <AlertTriangle size={13} className={styles.authAlert} aria-label="Sign-in failed" />
                  ) : (
                    <span
                      className={styles.freshness}
                      data-state={state}
                      role="img"
                      aria-label={freshnessTitle(state, account, nowSec)}
                      title={freshnessTitle(state, account, nowSec)}
                    />
                  )}
                  <span className={styles.count} data-numeric>
                    {countLabel(n)}
                  </span>
                </button>

                {state === 'auth' && (
                  <button
                    type="button"
                    className={styles.reconnect}
                    onClick={go(() => setSettingsOpen(true))}
                  >
                    Reconnect {account.email}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.group}>
          <div className={styles.groupLabel}>Mailboxes</div>
          {VIEWS.map(({ view: v, label, icon: Icon }) => (
            <button
              key={v}
              type="button"
              className={styles.row}
              data-active={view === v && category === null}
              onClick={go(() => {
                setCategory(null);
                setView(v);
              })}
            >
              <Icon size={16} className={`lucide ${styles.icon}`} aria-hidden="true" />
              <span className={styles.label}>{label}</span>
              {v === 'unread' && (
                <span className={styles.count} data-numeric>
                  {countLabel(unread.total)}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.group}>
          <div className={styles.groupLabel}>Splits</div>
          {CATEGORIES.map(({ category: c, label, icon: Icon }) => (
            <button
              key={c}
              type="button"
              className={styles.row}
              data-active={category === c}
              onClick={go(() => {
                setView('inbox');
                setCategory(c);
              })}
            >
              <Icon size={16} className={`lucide ${styles.icon}`} aria-hidden="true" />
              <span className={styles.label}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

export default AppRail;
